/**
 * История и сравнение с конкурентами.
 *
 * Ряд во времени — сердцевина продукта: на нём стоит динамика, на нём же
 * держится довод «сделали по рекомендации — цифра выросла». Отсюда правила,
 * которые здесь исполняются буквально (data-viz.md §4):
 *
 * * **Ось Y начинается с нуля.** Усечённая шкала в измерительном продукте —
 *   подлог, а не приём оформления.
 * * **Ряд называется в подписи.** Замеры идут еженедельно, и переключателя
 *   рядов здесь больше нет: ежедневный пульс из продукта убран. Подпись при
 *   этом осталась, потому что строки, посчитанные по пульсу раньше, никуда не
 *   делись, а у переписи и пульса разные знаменатели — склейка рисовала бы
 *   провал на каждом стыке.
 * * **Разрыв рвёт линию** и никогда не проводится через ноль.
 * * **Пунктир означает ровно одно** — незавершённый цикл.
 * * **Конкуренты различаются подписью на конце линии**, а не цветом: больше
 *   трёх линий одного цвета не различит никто, поэтому их не больше «топ-3 и
 *   вы».
 *
 * Сравнение периодов приходит с сервера уже посчитанным по **тем же
 * округлённым числам, которые клиент видел**. Если между окнами менялось ядро,
 * сервер помечает сравнение недостоверным — экран говорит это словами, а не
 * рисует красивую стрелку.
 */

import { useState } from 'react';

import { WithBrand } from '../ui/screen';
import { isoDate } from '@shared/src/intl';

import { useRead, daysAgo, type Page } from '../ui/query';
import { PeriodPicker, engineTitles } from '../ui/controls';
import type { PeriodKey } from '../ui/controls';
import { ErrorNotice, Skeleton } from '../ui/state';
import { NoDataInPeriod } from '../ui/screen-states';
import { Pill, Stamp, Trend } from '../ui/viz';
import { brandSeries, rivalSeriesOf } from '../ui/series';
import type { Series, SeriesPoint, TrendEvent } from '../ui/viz';
import { timelineEventKind } from '../ui/labels';
import {
  NOT_MEASURED,
  coverage,
  coverageOf,
  dateNumeric,
  deltaPoints,
  indexDelta,
  percent,
  pluralize,
  period as periodFmt,
  position,
} from '@shared/src/format';
import type { components } from '../api/schema';
import { engineName } from '@shared/src/product';

type DailySnapshot = components['schemas']['DailySnapshot'];
type BrandIndexPoint = components['schemas']['BrandIndexPoint'];
type CompetitorSnapshot = components['schemas']['CompetitorSnapshot'];
type PeriodComparison = components['schemas']['PeriodComparison'];
type TimelineEvent = components['schemas']['TimelineEvent'];
type EngineWithHealth = components['schemas']['EngineWithHealth'];

/** Линий на графике: предмет замера и не больше трёх соперников. */
const RIVAL_LINES = 3;

export function History() {
  return (
    <WithBrand title="Динамика">{(brandId) => <Body brandId={brandId} />}</WithBrand>
  );
}

function Body({ brandId }: { brandId: string }) {
  const [days, setDays] = useState<PeriodKey>('30');

  const to = isoDate(new Date());
  const from = daysAgo(Number(days));
  // Ряд один — недельная перепись. Переключателя «Перепись / Пульс» здесь
  // больше нет: ежедневный пульс уходит из продукта, и оставленный контрол
  // предлагал бы ряд, которого не будет.
  const window = { from, to, basis: 'weekly' as const };

  const engines = useRead<EngineWithHealth[]>('/engines');
  const daily = useRead<DailySnapshot[]>(`/brands/${brandId}/metrics/daily`, window);
  const index = useRead<BrandIndexPoint[]>(`/brands/${brandId}/metrics/index`, window);
  const rivals = useRead<CompetitorSnapshot[]>(
    `/brands/${brandId}/metrics/competitors`,
    window,
  );
  const events = useRead<Page<TimelineEvent>>(`/brands/${brandId}/timeline`, {
    from,
    to,
    limit: 50,
  });
  const comparison = useRead<PeriodComparison>(`/brands/${brandId}/comparison`, {
    current_from: from,
    current_to: to,
    basis: 'weekly' as const,
  });

  const tools = <PeriodPicker value={days} onChange={setDays} />;

  if (daily.kind === 'failed')
    return (
      <>
        {tools}
        <ErrorNotice error={daily.error} />
      </>
    );
  if (daily.kind === 'loading') {
    return (
      <>
        <div className="filters">{tools}</div>
        <Skeleton rows={3} />
      </>
    );
  }

  const rows = daily.data;
  const titles = engineTitles(engines.kind === 'ready' ? engines.data : []);

  if (rows.length === 0) {
    return (
      <>
        <div className="filters">{tools}</div>
        <NoDataInPeriod from={from} to={to} />
      </>
    );
  }

  // Свою линию собираем из тех же счётчиков, из которых считается видимость:
  // упоминания делятся на органические ответы. Это не вторая формула, а та же
  // самая — иначе точка на графике разошлась бы с числом на обзоре.
  const own = brandSeries(rows);
  const rivalSeries = rivalSeriesOf(rivals.kind === 'ready' ? rivals.data : []);

  const marks: TrendEvent[] =
    events.kind === 'ready'
      ? events.data.items.map((event) => ({
          id: event.id,
          date: event.occurred_on,
          // Незнакомый вид — не пустая подпись: перечисление растёт в контракте
          // и приезжает сюда копией, а отметка на графике обязана быть названа
          // хоть как-то раньше, чем кабинет пересоберут.
          label: timelineEventKind(event.kind),
          note: event.title,
        }))
      : [];

  // Строка снимка — это ДВИЖОК ЗА СУТКИ, а не точка графика: точка на графике
  // одна на дату. Подпись считала строки и за один день с пятью движками
  // сообщала «5 из 5 точек полные», хотя точка была ровно одна. День считается
  // полным, когда полны все его строки.
  const dayComplete = new Map<string, boolean>();
  for (const row of rows) {
    const seen = dayComplete.get(row.snapshot_date);
    dayComplete.set(row.snapshot_date, (seen ?? true) && Boolean(row.is_complete));
  }
  const dayCount = dayComplete.size;
  const completeDays = [...dayComplete.values()].filter(Boolean).length;
  const stamp = (
    <>
      {periodFmt(from, to)} · полная сетка в {coverage(completeDays, dayCount)}{' '}
      {pluralize(dayCount, 'дня', 'дней', 'дней')}
    </>
  );

  const series: Series[] = [
    { key: 'own', name: 'Вы', points: own, subject: true },
    ...rivalSeries.slice(0, RIVAL_LINES),
  ];

  const indexPoints: SeriesPoint[] =
    index.kind === 'ready'
      ? index.data
          .slice()
          .sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date))
          .map((point) => ({
            date: point.snapshot_date,
            value: point.score,
            incomplete: point.is_complete === false,
          }))
      : [];

  return (
    <>
      <div className="filters">{tools}</div>
      <Stamp>{stamp}</Stamp>

      <section className="block">
        <h2>Видимость и кто рядом</h2>
        <Trend series={series} events={marks} />
      </section>

      <section className="block">
        <h2>Индекс ИИ-видимости</h2>
        {index.kind === 'loading' ? (
          <Skeleton rows={2} />
        ) : (
          <Trend
            series={[{ key: 'index', name: 'Балл', points: indexPoints, subject: true }]}
            events={marks}
            unit=""
            fixedTop={100}
            emptyText="Балл появится после первой полной сетки за выбранный ряд."
          />
        )}
      </section>

      <Comparison comparison={comparison} from={from} to={to} titles={titles} />

      <section className="block">
        <h2>Суточные снимки</h2>
        <div className="tbl-wrap">
          <table className="mini tbl-dense">
            <caption>Снимки по движкам и суткам</caption>
            <thead>
              <tr>
                <th scope="col">Дата</th>
                <th scope="col">Движок</th>
                <th scope="col" className="num" title="Упоминаний на органический ответ">
                  Видимость
                </th>
                <th scope="col" className="num" title="Доля упоминаний в первой тройке">
                  В топ-3
                </th>
                <th scope="col" className="num" title="Среднее место. Меньше — лучше">
                  Позиция
                </th>
                <th scope="col" className="num">
                  Ответов
                </th>
                <th scope="col">Сетка</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .slice()
                .sort(
                  (a, b) =>
                    b.snapshot_date.localeCompare(a.snapshot_date) ||
                    a.engine_code.localeCompare(b.engine_code),
                )
                .slice(0, 60)
                .map((row) => (
                  <tr key={`${row.snapshot_date}-${row.engine_code}`}>
                    <td className="tnum">{dateNumeric(row.snapshot_date)}</td>
                    <td>{engineName(row.engine_code, titles.get(row.engine_code))}</td>
                    <td className="tnum num">
                      {row.visibility === null || row.visibility === undefined
                        ? NOT_MEASURED
                        : percent(row.visibility, 1)}
                    </td>
                    <td className="tnum num">
                      {row.top3_share === null || row.top3_share === undefined
                        ? NOT_MEASURED
                        : percent(row.top3_share, 1)}
                    </td>
                    <td className="tnum num">
                      {row.avg_position === null || row.avg_position === undefined
                        ? NOT_MEASURED
                        : position(row.avg_position)}
                    </td>
                    <td className="tnum num">
                      {coverage(row.organic_mentions ?? 0, row.organic_answers ?? 0)}
                    </td>
                    <td className="tnum">
                      {row.cells_planned
                        ? `${coverage(row.cells_filled ?? 0, row.cells_planned)}${row.is_complete ? '' : ' · неполная'}`
                        : NOT_MEASURED}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <p className="tbl-foot stamp">
          Показано{' '}
          {coverageOf(
            Math.min(60, rows.length),
            rows.length,
            'строка',
            'строки',
            'строк',
          )}{' '}
          за период.
        </p>
      </section>
    </>
  );
}

/* ────────────────────────── сравнение периодов ────────────────────────────── */

function Comparison({
  comparison,
  from,
  to,
  titles,
}: {
  comparison: ReturnType<typeof useRead<PeriodComparison>>;
  from: string;
  to: string;
  titles: Map<string, string>;
}) {
  if (comparison.kind === 'loading') {
    return (
      <section className="block">
        <h2>Стало лучше или хуже</h2>
        <Skeleton rows={1} />
      </section>
    );
  }
  if (comparison.kind === 'failed') {
    return (
      <section className="block">
        <h2>Стало лучше или хуже</h2>
        <ErrorNotice error={comparison.error} />
      </section>
    );
  }

  const data = comparison.data;
  const base = data.baseline;

  return (
    <section className="block">
      <h2>Стало лучше или хуже</h2>
      {!data.is_comparable ? (
        <div className="notice warn" role="note">
          <b>Сравнивать эти два окна нельзя</b>
          {data.incomparable_reason ??
            'Между окнами менялось семантическое ядро или сетка закрылась не целиком. Разница чисел в таком сравнении говорила бы не о бренде, а о смене вопросов.'}
        </div>
      ) : null}

      <div className="effect">
        <div>
          <b>Было</b>
          <span className="tnum">
            {base?.visibility === null || base?.visibility === undefined
              ? NOT_MEASURED
              : percent(base.visibility, 1)}
          </span>
          <p className="stamp">
            {base ? periodFmt(base.from, base.to) : 'предыдущего окна нет'}
          </p>
        </div>
        <div>
          <b>Стало</b>
          <span className="tnum">
            {data.current.visibility === null || data.current.visibility === undefined
              ? NOT_MEASURED
              : percent(data.current.visibility, 1)}
          </span>
          <p className="stamp">{periodFmt(from, to)}</p>
        </div>
        <div>
          <b>Видимость</b>
          <span>
            <Pill
              value={data.is_comparable ? data.visibility_delta_pp : null}
              text={
                data.is_comparable &&
                data.visibility_delta_pp !== null &&
                data.visibility_delta_pp !== undefined
                  ? deltaPoints(data.visibility_delta_pp, 1)
                  : 'сравнение недостоверно'
              }
            />
          </span>
        </div>
        <div>
          <b>Индекс</b>
          <span>
            <Pill
              value={data.is_comparable ? data.index_delta : null}
              text={
                data.is_comparable &&
                data.index_delta !== null &&
                data.index_delta !== undefined
                  ? // Без единицы вовсе. Индекс — единственная метрика
                    // продукта, у которой её нет (data-viz.md §8): он не
                    // проценты и не процентные пункты. Слово «балла» здесь
                    // было вшито строкой и не склонялось — «+11 балла»,
                    // «+2 балла», — то есть строка была верна ровно при одном
                    // значении из всех.
                    indexDelta(data.index_delta)
                  : 'сравнение недостоверно'
              }
            />
          </span>
        </div>
      </div>

      {data.by_engine && data.by_engine.length > 0 ? (
        <div className="tbl-wrap">
          <table className="mini">
            <caption>Видимость по движкам, было и стало</caption>
            <thead>
              <tr>
                <th scope="col">Движок</th>
                <th scope="col" className="num">
                  Было
                </th>
                <th scope="col" className="num">
                  Стало
                </th>
                <th scope="col" className="num">
                  Разница
                </th>
              </tr>
            </thead>
            <tbody>
              {data.by_engine.map((row) => (
                <tr key={row.engine_code}>
                  <td>{engineName(row.engine_code, titles.get(row.engine_code))}</td>
                  <td className="tnum num">
                    {row.visibility_baseline === null ||
                    row.visibility_baseline === undefined
                      ? NOT_MEASURED
                      : percent(row.visibility_baseline, 1)}
                  </td>
                  <td className="tnum num">
                    {row.visibility_current === null ||
                    row.visibility_current === undefined
                      ? NOT_MEASURED
                      : percent(row.visibility_current, 1)}
                  </td>
                  <td className="num">
                    {row.delta_pp === null || row.delta_pp === undefined ? (
                      <span className="stamp">{NOT_MEASURED}</span>
                    ) : (
                      <Pill value={row.delta_pp} text={deltaPoints(row.delta_pp, 1)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
